/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, January 11th 2022, 12:18:21 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Stack from "@mui/material/Stack";
import { FixedRightSlidePanel } from "common/components";
import { useDeepMemo } from "lib/deep_hooks";
import { getDefaultTaskTemplate } from "lib/factories";
import { ProcessDefinition, TaskTemplate, NODE_TYPES, ProcessStage } from "lib/schemas";
import { TaskTemplateForm } from "service-builder/components/workflow-builder/TaskTemplateForm";
import { BPMNPopUpTaskTypeSelector } from "service-builder/components/workflow-builder/TaskTypePopupSelector";
import { useWorkflowContext } from "service-builder/providers";
import { useWorkflowStore } from "service-builder/stores";
import { useToggleState } from "ui-widgets";
import { OSDropZone } from "ui-widgets/drag-and-drop";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import { BPMNNodeTypeDragLayer } from "./BPMNNodeTypeDragLayer";
import { DeleteTaskTemplateComponent } from "./DeleteTaskTemplateComponent";
import { NewTaskInfo } from "./NewTaskInfo";
import { NewTaskTemplateDropTargetWrapper } from "./NewTaskTemplateDropTargetWrapper";
import { IWorkflowViewType } from "./types";
import { WorkflowBuilderTopBar } from "./WorkflowBuilderTopBar";
import { WorkflowAsChart } from "./WorkflowAsChart";
import { WorkflowAsList } from "./WorkflowAsList";

interface WorkflowBuilderProps {
  processDefinition: ProcessDefinition;
}

export const WorkflowBuilder = ({ processDefinition }: WorkflowBuilderProps) => {
  const [data, handlers] = useWorkflowBuilderLogic({ processDefinition });

  const {
    currentMutationItem,
    taskTemplateFormOpenState,
    workflowViewType,
    savedTaskTemplates,
    isFetchingItems,
    fetchItemsStatusCode,
    fetchItemsHasError,
    processDefinitionHasStartAction,
    bpmnTypeSelectorOpenState,
    newTaskInfoOpenState,
    newTaskType,
    selectedProcessStageId,
    openDelete,
  } = data;

  const {
    closeTaskTemplateFormFn,
    updateListAndSelectIfNewItem,
    setWorkflowViewType,
    handleClickTaskTemplate,
    handleCreateNewTaskTemplate,
    createNewTaskTemplate,
    openBPMNTypeSelectorFn,
    closeBPMNTypeSelectorFn,
    closeNewTaskInfoFn,
    handleSearch,
    handleFilterByStage,
    handleOpenDelete,
    handleCloseDelete,
  } = handlers;

  let workflowViewComponent: React.ReactNode = null;
  switch (workflowViewType) {
    case "nodeList":
      workflowViewComponent = <WorkflowAsList handleClickTaskTemplate={handleClickTaskTemplate} />;
      break;
    case "flowChart":
      workflowViewComponent = <WorkflowAsChart handleClickTaskTemplate={handleClickTaskTemplate} />;
      break;
  }

  return (
    <>
      {/** Placing drag layer here to avoid it being affected by the fixed css position of the left panel. Apparently there is a known source of unpredictable behaviour with children of elements that have fixed css position - https://stackoverflow.com/questions/21091958/css-fixed-child-element-positions-relative-to-parent-element-not-to-the-viewpo  */}
      <BPMNNodeTypeDragLayer />

      <NewTaskTemplateDropTargetWrapper onDropTaskTemplateType={handleCreateNewTaskTemplate}>
        <APIListProgressWrapper
          showMessageWhenEmpty={false}
          items={savedTaskTemplates}
          showLoader={isFetchingItems}
          httpResponseStatusCode={fetchItemsStatusCode}
          isAPIError={fetchItemsHasError}
        >
          <BPMNPopUpTaskTypeSelector
            hideStartTaskTypeOption={processDefinitionHasStartAction}
            handleTaskTypeSelection={handleCreateNewTaskTemplate}
            open={bpmnTypeSelectorOpenState}
            onClose={closeBPMNTypeSelectorFn}
          />

          <NewTaskInfo
            open={newTaskInfoOpenState}
            onClose={closeNewTaskInfoFn}
            createNewTaskTemplate={createNewTaskTemplate}
            nodeType={newTaskType}
          />

          <DeleteTaskTemplateComponent openDelete={openDelete} handleCloseDelete={handleCloseDelete} />

          <Stack spacing={2}>
            {/* {creationQueueHasItems ? null : ( */}
            <OSDropZone
              style={{
                // transform: `translateY(${endOfListPosition * 100}%)`,
                height: 87,
              }}
            >
              Drag and Drop task types from the toolbar to add a step to the workflow.
            </OSDropZone>
            {/* )} */}

            <WorkflowBuilderTopBar
              openBPMNTypeSelectorFn={openBPMNTypeSelectorFn}
              handleSearch={handleSearch}
              handleFilterByStage={handleFilterByStage}
              selectedProcessStageId={selectedProcessStageId}
              workflowViewType={workflowViewType}
              setWorkflowViewType={setWorkflowViewType}
            />

            {workflowViewComponent}

            {currentMutationItem ? (
              <FixedRightSlidePanel open={taskTemplateFormOpenState} onClose={closeTaskTemplateFormFn}>
                <TaskTemplateForm
                  taskTemplateData={currentMutationItem}
                  taskTemplateSaveCallbackFn={updateListAndSelectIfNewItem}
                  handleDeleteTaskTemplate={handleOpenDelete}
                />
              </FixedRightSlidePanel>
            ) : null}
          </Stack>
        </APIListProgressWrapper>
      </NewTaskTemplateDropTargetWrapper>
    </>
  );
};

const useWorkflowBuilderLogic = ({ processDefinition }) => {
  const taskTemplateFormOpenState = useWorkflowStore((state) => state.taskTemplateFormOpenState);
  const closeTaskTemplateFormFn = useWorkflowStore((state) => state.closeTaskTemplateFormFn);
  const openTaskTemplateFormFn = useWorkflowStore((state) => state.openTaskTemplateFormFn);

  const {
    open: bpmnTypeSelectorOpenState,
    handleClose: closeBPMNTypeSelectorFn,
    handleOpen: openBPMNTypeSelectorFn,
  } = useToggleState();

  const {
    open: newTaskInfoOpenState,
    handleClose: closeNewTaskInfoFn,
    handleOpen: openNewTaskInfoFn,
  } = useToggleState();

  const [newTaskType, setNewTaskType] = React.useState<keyof typeof NODE_TYPES>();

  const [workflowViewType, setWorkflowViewType] = React.useState<IWorkflowViewType>("flowChart");

  const {
    taskTemplateListContext: {
      itemsListState,
      isFetchingItems,
      fetchItemsHasError,
      fetchItemsStatusCode,
      currentMutationItem,
      endOfListPosition,
      createNewItem,
      callFilteringFunction,
      updateListAndSelectIfNewItem,
      selectItem,
    },
  } = useWorkflowContext();

  const { savedItems: savedTaskTemplates } = itemsListState;

  const createNewTaskTemplate = React.useCallback(
    (type: keyof typeof NODE_TYPES, title: string, optionalProperties?: Partial<TaskTemplate>) => {
      const newTaskTemplateSequence = endOfListPosition;

      const newTaskTemplate = getDefaultTaskTemplate({
        processDefinition,
        optionalProperties,
        title,
        bpmnNodeType: type,
        defaultSequence: newTaskTemplateSequence,
      });

      createNewItem(newTaskTemplate);

      closeBPMNTypeSelectorFn();
    },
    [processDefinition, createNewItem, closeBPMNTypeSelectorFn, endOfListPosition]
  );

  const handleSearch = React.useCallback((search_text: string) => {
    callFilteringFunction((taskTemplates) =>
      taskTemplates.filter((t) => t.title.toString().toLowerCase().includes(search_text.toLowerCase())).map((t) => t.id)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedProcessStageId, setSelectedProcessStageId] = React.useState<ProcessStage["id"]>();

  const handleFilterByStage = React.useCallback((processStageId: ProcessStage["id"]) => {
    callFilteringFunction((taskTemplates) => {
      if (!processStageId) {
        setSelectedProcessStageId(null);
        return null;
      }

      setSelectedProcessStageId(processStageId);
      return taskTemplates.filter((t) => t.stage === processStageId).map((t) => t.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processDefinitionHasStartAction = useDeepMemo(
    () => savedTaskTemplates.some((t) => t.type === "StartFunction"),
    [savedTaskTemplates]
  );

  const handleCreateNewTaskTemplate = (type: keyof typeof NODE_TYPES, title: string) => {
    switch (type) {
      case "View":
      case "Handler":
        setNewTaskType(type);
        openNewTaskInfoFn();
        return;
      default:
        createNewTaskTemplate(type, title);
    }
  };

  const { open: openDelete, handleClose: handleCloseDelete, handleOpen: handleOpenDelete } = useToggleState();

  const handleClickTaskTemplate = React.useCallback(
    (selectedTaskTemplate: TaskTemplate) => {
      selectItem(selectedTaskTemplate.id);
      openTaskTemplateFormFn();
    },
    [selectItem, openTaskTemplateFormFn]
  );

  return [
    {
      currentMutationItem,
      taskTemplateFormOpenState,
      workflowViewType,
      savedTaskTemplates,
      isFetchingItems,
      fetchItemsStatusCode,
      fetchItemsHasError,
      processDefinitionHasStartAction,
      bpmnTypeSelectorOpenState,
      newTaskInfoOpenState,
      newTaskType,
      selectedProcessStageId,
      openDelete,
    },
    {
      closeTaskTemplateFormFn,
      updateListAndSelectIfNewItem,
      setWorkflowViewType,
      handleClickTaskTemplate,
      handleCreateNewTaskTemplate,
      createNewTaskTemplate,
      openBPMNTypeSelectorFn,
      closeBPMNTypeSelectorFn,
      closeNewTaskInfoFn,
      handleSearch,
      handleFilterByStage,
      handleOpenDelete,
      handleCloseDelete,
    },
  ];
};
